/**
 * Zones API (AVA-181) — envizion.reality.api `api/v1/zones` (ZonesController).
 *
 * The API owns the semantics: retirement (including scheduled retirement), containment for
 * "Find records inside boundary" and record permission filtering. A zone belongs to one facility or to
 * one facility group (`scope`: 'facility' | 'group'); a group zone applies in each of its facilities.
 * Calls that read records are scoped to the current facility (`store.state.assetId`) unless a facility
 * id is passed: that is the facility a group zone's records are checked in.
 *
 * Every method resolves to the axios response (`{ data }`) and rejects with an Error whose message
 * is the API's, so callers can show `err.message` directly.
 */
import httpService from '@Utils/httpService';
import store from '@/store';

const baseUrl = 'api/v1/zones';

const currentFacilityId = () => store.state.assetId;

// The API answers 400 with { message } (ErrorResponseMessage) and 404 with a plain string.
function apiError(err) {
  const data = err?.response?.data;
  const message = (typeof data === 'string' && data) || data?.message || data?.Message || err?.message || 'Zones request failed.';
  const wrapped = new Error(message);
  wrapped.status = err?.response?.status;
  // 409 bodies carry what the user has to decide: { conflicts } or { violations }.
  wrapped.data = data && typeof data === 'object' ? data : null;
  return wrapped;
}

const call = (request) => request.catch((err) => Promise.reject(apiError(err)));

export default {
  getZoneTypes({ includeRetired = false } = {}) {
    return call(httpService.get({ url: `${baseUrl}/types`, params: { includeRetired } }));
  },

  // `type.fields`: [{ id?, label, fieldType: 'text'|'dropdown', options: [{ id?, value }] }]
  // Turning `allowMultipleAssignments` off is refused (409 { violations }) while records break it.
  saveZoneType(type) {
    const data = {
      name: type.name,
      description: type.description,
      allowMultipleAssignments: type.allowMultipleAssignments ?? null,
      fields: type.fields || [],
    };
    return type.id
      ? call(httpService.put({ url: `${baseUrl}/types/${type.id}`, data }))
      : call(httpService.post({ url: `${baseUrl}/types`, data }));
  },

  // Records in several zones of the type: { violations: [{ recordName, assignments }], hidden }.
  getTypeViolations(zoneTypeId) {
    return call(httpService.get({ url: `${baseUrl}/types/${zoneTypeId}/violations` }));
  },

  // Keeps one assignment per violating record, removes the rest, then allows only one zone.
  restrictTypeToSingle(zoneTypeId, keepAssignmentIds) {
    return call(httpService.post({ url: `${baseUrl}/types/${zoneTypeId}/restrict-to-single`, data: { keepAssignmentIds } }));
  },

  // The facilities and facility groups the user can give a zone: { facilities: [{ id, name, facilityGroupId }], groups: [{ id, name }] }.
  getOwners() {
    return call(httpService.get({ url: `${baseUrl}/owners` }));
  },

  // With a facility: the zones that apply there (its own and its group's). `facilityId: null` lists
  // every zone of the facilities and groups the user can open (company admin).
  getZones({ includeRetired = false, facilityId = currentFacilityId() } = {}) {
    return call(httpService.get({ url: baseUrl, params: { facilityId: facilityId || undefined, includeRetired } }));
  },

  // Editing keeps the ID and never touches assignments, even when the boundary moves. A new zone
  // belongs to `facilityGroupId` when set, else to `facilityId` (the current facility by default);
  // the owner never changes after that.
  saveZone(zone) {
    const owner = zone.facilityGroupId
      ? { facilityId: null, facilityGroupId: zone.facilityGroupId }
      : { facilityId: zone.facilityId || (zone.id ? null : currentFacilityId()), facilityGroupId: null };
    const data = {
      zoneTypeId: zone.zoneTypeId,
      ...owner,
      name: zone.name,
      description: zone.description,
      colour: zone.colour,
      attributes: zone.attributes || {},
      boundary: zone.boundary
        ? { polygon: zone.boundary.polygon, zMin: zone.boundary.zMin, zMax: zone.boundary.zMax, plan: zone.boundary.plan || null }
        : null,
    };
    return zone.id
      ? call(httpService.put({ url: `${baseUrl}/${zone.id}`, data }))
      : call(httpService.post({ url: baseUrl, data }));
  },

  /**
   * 'active' reactivates (and cancels any scheduled retirement). 'retired' retires now, or, with a
   * future `retireOn` (YYYY-MM-DD), keeps the zone active until that date.
   */
  setZoneStatus(zoneId, status, { retireOn = null } = {}) {
    return status === 'active'
      ? call(httpService.post({ url: `${baseUrl}/${zoneId}/reactivate` }))
      : call(httpService.post({ url: `${baseUrl}/${zoneId}/retire`, data: { retireOn } }));
  },

  // Explicitly assigned records the user may see: { tags: [], tasks: [] }.
  getZoneRecords(zoneId, facilityId = currentFacilityId()) {
    return call(httpService.get({ url: `${baseUrl}/${zoneId}/records`, params: { facilityId: facilityId || undefined } }));
  },

  // Distinct visible records across several zones: { distinct, perZoneSum }.
  getCombinedRecordCount(zoneIds, facilityId = currentFacilityId()) {
    return call(httpService.post({ url: `${baseUrl}/records/count`, data: { zoneIds, facilityId: facilityId || null } }));
  },

  // The active zones a tag or task is in (its card), each with the `assignmentId` to remove.
  getRecordZones(recordType, recordId, facilityId = currentFacilityId()) {
    return call(httpService.get({ url: `${baseUrl}/by-record/${recordType}/${encodeURIComponent(recordId)}`, params: { facilityId } }));
  },

  // The assign picker's tree: tag kinds and task boards the user may see, with matching counts:
  // { tags: [{ key, label, kind, count }], tasks: [{ key, label, colour, count }] }.
  getAssignableGroups(zoneId, search = '') {
    return call(httpService.get({ url: `${baseUrl}/${zoneId}/assignable/groups`, params: { search, facilityId: currentFacilityId() } }));
  },

  // Tags around the zone with positions, inside flag and zone state, for the plan view: { tags, truncated }.
  getZoneMap(zoneId) {
    return call(httpService.get({ url: `${baseUrl}/${zoneId}/map`, params: { facilityId: currentFacilityId() } }));
  },

  // The assign picker's quick preview of a task (ZoneTaskPreviewDto).
  getTaskPreview(zoneId, taskId) {
    return call(httpService.get({ url: `${baseUrl}/${zoneId}/task-preview/${taskId}`, params: { facilityId: currentFacilityId() } }));
  },

  // A page of one group, each record with its state in the zone (assigned, heldZoneName): { items, total }.
  getAssignableRecords(zoneId, { recordType, group, search = '', skip = 0, take = 50 }) {
    return call(httpService.get({
      url: `${baseUrl}/${zoneId}/assignable`,
      params: { recordType, group, search, skip, take, facilityId: currentFacilityId() },
    }));
  },

  /**
   * items: [{ zoneId, recordType: 'tag'|'task', recordId }] → { created, skipped, replaced }.
   * A record already in another zone of a one-zone-only type is refused (409 { conflicts }) unless
   * `replace` is the user's explicit decision to replace it. Records of a group zone are checked in
   * the current facility.
   */
  assign(items, { replace = false } = {}) {
    return call(httpService.post({ url: `${baseUrl}/assignments`, data: { items, replace, facilityId: currentFacilityId() } }));
  },

  unassign(assignmentIds) {
    return call(httpService.post({ url: `${baseUrl}/assignments/remove`, data: { assignmentIds } }));
  },
};
